/**
 * Enhanced Mega Menu for Header Integration
 * This script enhances the existing mega menu functionality in the header
 */

class EnhancedMegaMenu {
  constructor() {
    this.megaMenuSection = document.querySelector(".enhanced-mega-menu");
    this.menuItems = document.querySelectorAll(
      ".enhanced-mega-menu-list .wt-page-nav-mega__item--parent"
    );
    this.links = document.querySelectorAll(
      ".enhanced-mega-menu-list .wt-page-nav-mega__link"
    );
    this.dropdowns = document.querySelectorAll(
      ".enhanced-mega-menu .wt-page-nav-mega__sublist__wrapper"
    );
    this.isMobile = window.innerWidth <= 768;
    this.activeItem = null;
    this.touchStartY = 0;
    this.touchStartX = 0;

    this.init();
  }

  init() {
    if (!this.megaMenuSection) return;

    this.setupEventListeners();
    this.setupMobileMenu();
    this.setupKeyboardNavigation();
    this.setupAccessibility();
    this.setupTouchGestures();
    this.setupResizeHandler();
    this.setupClickOutside();
  }

  setupEventListeners() {
    // Menu item hover events
    this.menuItems.forEach((item) => {
      const link = item.querySelector(".wt-page-nav-mega__link");
      const dropdown = item.querySelector(
        ".wt-page-nav-mega__sublist__wrapper"
      );

      if (link && dropdown) {
        // Mouse events
        item.addEventListener("mouseenter", () => {
          if (!this.isMobile) {
            this.showDropdown(item);
          }
        });

        item.addEventListener("mouseleave", () => {
          if (!this.isMobile) {
            this.hideDropdown(item);
          }
        });

        // Click events for mobile
        link.addEventListener("click", (e) => {
          if (this.isMobile) {
            e.preventDefault();
            this.toggleMobileDropdown(item);
          }
        });
      }
    });

    // Link click events
    this.links.forEach((link) => {
      link.addEventListener("click", (e) => {
        if (
          !this.isMobile ||
          !link.closest(".wt-page-nav-mega__item--parent")
        ) {
          // Allow normal navigation for non-dropdown links
          return;
        }
      });
    });
  }

  setupMobileMenu() {
    if (!this.megaMenuSection) return;

    // Add mobile-specific classes and behaviors
    if (this.isMobile) {
      this.megaMenuSection.classList.add("mobile-menu");

      // Close all dropdowns on mobile by default
      this.menuItems.forEach((item) => {
        item.classList.remove("active");
      });
    } else {
      this.megaMenuSection.classList.remove("mobile-menu");
    }
  }

  setupKeyboardNavigation() {
    this.links.forEach((link) => {
      link.addEventListener("keydown", (e) => {
        switch (e.key) {
          case "Enter":
          case " ":
            e.preventDefault();
            if (this.isMobile) {
              const item = link.closest(".wt-page-nav-mega__item");
              if (
                item &&
                item.classList.contains("wt-page-nav-mega__item--parent")
              ) {
                this.toggleMobileDropdown(item);
              }
            } else {
              link.click();
            }
            break;
          case "Escape":
            this.hideAllDropdowns();
            link.focus();
            break;
          case "ArrowDown":
            e.preventDefault();
            this.focusNextItem(link);
            break;
          case "ArrowUp":
            e.preventDefault();
            this.focusPreviousItem(link);
            break;
        }
      });
    });
  }

  setupAccessibility() {
    this.menuItems.forEach((item) => {
      const link = item.querySelector(".wt-page-nav-mega__link");
      const dropdown = item.querySelector(
        ".wt-page-nav-mega__sublist__wrapper"
      );

      if (link && dropdown) {
        // Set ARIA attributes
        link.setAttribute("aria-haspopup", "true");
        link.setAttribute("aria-expanded", "false");

        // Set up dropdown ARIA
        dropdown.setAttribute("role", "menu");
        dropdown.setAttribute(
          "aria-labelledby",
          link.id || this.generateId(link)
        );

        // Ensure link has an ID for ARIA
        if (!link.id) {
          link.id = this.generateId(link);
        }
      }
    });
  }

  setupTouchGestures() {
    if (!this.megaMenuSection) return;

    // Touch events for mobile
    this.megaMenuSection.addEventListener(
      "touchstart",
      (e) => {
        this.touchStartY = e.touches[0].clientY;
        this.touchStartX = e.touches[0].clientX;
      },
      { passive: true }
    );

    this.megaMenuSection.addEventListener(
      "touchmove",
      (e) => {
        if (this.isMobile) {
          const touchY = e.touches[0].clientY;
          const touchX = e.touches[0].clientX;
          const deltaY = Math.abs(touchY - this.touchStartY);
          const deltaX = Math.abs(touchX - this.touchStartX);

          // If vertical scroll is greater than horizontal, prevent horizontal scroll
          if (deltaY > deltaX) {
            e.preventDefault();
          }
        }
      },
      { passive: false }
    );
  }

  setupResizeHandler() {
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth <= 768;

        if (wasMobile !== this.isMobile) {
          this.handleResponsiveChange();
        }
      }, 250);
    });
  }

  setupClickOutside() {
    document.addEventListener("click", (e) => {
      if (!this.megaMenuSection?.contains(e.target)) {
        this.hideAllDropdowns();
      }
    });
  }

  showDropdown(item) {
    this.hideAllDropdowns();
    item.classList.add("active");
    this.activeItem = item;

    const link = item.querySelector(".wt-page-nav-mega__link");
    const dropdown = item.querySelector(".wt-page-nav-mega__sublist__wrapper");

    if (link && dropdown) {
      link.setAttribute("aria-expanded", "true");
      dropdown.setAttribute("aria-hidden", "false");

      // Focus first focusable element in dropdown
      const firstFocusable = dropdown.querySelector(
        'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        setTimeout(() => firstFocusable.focus(), 100);
      }
    }
  }

  hideDropdown(item) {
    item.classList.remove("active");

    const link = item.querySelector(".wt-page-nav-mega__link");
    const dropdown = item.querySelector(".wt-page-nav-mega__sublist__wrapper");

    if (link && dropdown) {
      link.setAttribute("aria-expanded", "false");
      dropdown.setAttribute("aria-hidden", "true");
    }

    if (this.activeItem === item) {
      this.activeItem = null;
    }
  }

  hideAllDropdowns() {
    this.menuItems.forEach((item) => {
      this.hideDropdown(item);
    });
  }

  toggleMobileDropdown(item) {
    const isActive = item.classList.contains("active");

    // Close all other dropdowns
    this.menuItems.forEach((otherItem) => {
      if (otherItem !== item) {
        this.hideDropdown(otherItem);
      }
    });

    // Toggle current item
    if (isActive) {
      this.hideDropdown(item);
    } else {
      this.showDropdown(item);
    }
  }

  focusNextItem(currentLink) {
    const currentIndex = Array.from(this.links).indexOf(currentLink);
    const nextIndex = (currentIndex + 1) % this.links.length;
    this.links[nextIndex].focus();
  }

  focusPreviousItem(currentLink) {
    const currentIndex = Array.from(this.links).indexOf(currentLink);
    const prevIndex =
      currentIndex === 0 ? this.links.length - 1 : currentIndex - 1;
    this.links[prevIndex].focus();
  }

  handleResponsiveChange() {
    this.hideAllDropdowns();

    if (this.isMobile) {
      this.megaMenuSection?.classList.add("mobile-menu");
    } else {
      this.megaMenuSection?.classList.remove("mobile-menu");
    }
  }

  generateId(element) {
    const text = element.textContent.trim().toLowerCase().replace(/\s+/g, "-");
    return `enhanced-mega-menu-${text}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
  }

  // Public methods for external control
  openDropdown(item) {
    if (item && item.classList.contains("wt-page-nav-mega__item--parent")) {
      this.showDropdown(item);
    }
  }

  closeDropdown(item) {
    if (item) {
      this.hideDropdown(item);
    }
  }

  closeAllDropdowns() {
    this.hideAllDropdowns();
  }

  // Method to update menu content dynamically
  updateMenu(menuData) {
    console.log("Enhanced menu update requested:", menuData);
    // Implementation would depend on specific requirements
  }

  // Cleanup method
  destroy() {
    this.hideAllDropdowns();
    // Remove event listeners if needed
  }
}

// Auto-initialize enhanced mega menu
document.addEventListener("DOMContentLoaded", () => {
  // Wait for the mega menu to be available
  const checkForMegaMenu = () => {
    const megaMenuSection = document.querySelector(".enhanced-mega-menu");
    if (megaMenuSection) {
      new EnhancedMegaMenu();
    } else {
      // Retry after a short delay
      setTimeout(checkForMegaMenu, 100);
    }
  };

  checkForMegaMenu();
});

// Export for use in other scripts
if (typeof module !== "undefined" && module.exports) {
  module.exports = EnhancedMegaMenu;
}

// Global access
window.EnhancedMegaMenu = EnhancedMegaMenu;
